/**
 * NetworkMonitor Component
 * 
 * A headless component that monitors network connectivity and updates the Zustand store.
 * This replaces the NetworkContext with a Zustand-based approach.
 * 
 * Place this component at the root of your app (in main.tsx or App.tsx).
 * 
 * @example
 * function App() {
 *   return (
 *     <>
 *       <NetworkMonitor />
 *       <Router>
 *         ...
 *       </Router>
 *     </>
 *   );
 * }
 */

import { useEffect, useCallback } from 'react';
import { useBoundStore } from '@/stores';

export function NetworkMonitor(): null {
    const setIsOnline = useBoundStore.use.setIsOnline();

    const updateNetworkStatus = useCallback(() => {
        setIsOnline(navigator.onLine);
    }, [setIsOnline]);

    useEffect(() => {
        // Set up event listeners
        window.addEventListener('online', updateNetworkStatus);
        window.addEventListener('offline', updateNetworkStatus);

        // Set initial state
        updateNetworkStatus();

        return () => {
            window.removeEventListener('online', updateNetworkStatus);
            window.removeEventListener('offline', updateNetworkStatus);
        };
    }, [updateNetworkStatus]);

    // This is a headless component - renders nothing
    return null;
}

export default NetworkMonitor;
